import { customerOrganisation } from "./enterpriseCustomer";
import { useState, type ReactNode } from "react";
import { Navigate } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Translate, useTranslation } from "@privacy/translations";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DynamicGrid } from "../dynamicGrid";
import { Drawer } from "../shared/Drawer";
import { PlusIcon, TenantIcon2, TrashCanIcon } from "../shared/icons";
import { usePermission } from "../shared/permissions";
import { usePrivacyNavigate } from "../shared/usePrivacyNavigate";
import { useCurrentCustomer } from "../root/CurrentOrganisationProvider";
import {
  removeSharedProcessingFromOrganisationMutationOptions,
  sharedProcessingByIdQueryOptions,
  sharedProcessingSharedOrganisationsQueryOptions,
  type SharedOrganisations,
} from "../api/sharedProcessings";
import { STATUS_WORDS } from "../shared/StatusWord";
import type { RegistrationStatus } from "../api/registrations.types";
import { StatusOptionLabel, StatusSquare } from "../shared/RegistrationStatusDot";
import { Strong } from "../shared/Strong";
import { SharedProcessingDrawerHeader } from "./SharedProcessingDrawerHeader";
import { ShareSharedProcessingDialog } from "./ShareSharedProcessingDialog";
import { RemoveSharedProcessingDialog } from "./RemoveSharedProcessingDialog";
import { hasErrorCode } from "../api/errors";
import { queryClient } from "../api/queryClient";
import { responsibleCandidatesForTenantsQueryOptions } from "../api/registrations";

const prefetchResponsibleCandidates = (organisations: Array<{ organisationId: string }>) =>
  organisations.forEach(
    organisation =>
      void queryClient.prefetchQuery(responsibleCandidatesForTenantsQueryOptions([organisation.organisationId]))
  );

type SharedOrganisation = SharedOrganisations[number];

export const SharedProcessingShareSubDrawer = () => {
  const sharedProcessingId = DynamicGrid.useSelectedRowId();
  const { hasPermission } = usePermission();
  const { paths } = usePrivacyNavigate();

  if (!hasPermission("nova-forms-api:enterprise-customers:registrations:manage")) {
    return <Navigate to={paths.sharedProcessingsInline(sharedProcessingId)} replace />;
  }

  return <PresentInSubDrawerContent key={sharedProcessingId} sharedProcessingId={sharedProcessingId} />;
};

const PresentInSubDrawerContent = ({ sharedProcessingId }: { sharedProcessingId: string }) => {
  const { t } = useTranslation();
  const { navigate } = usePrivacyNavigate();
  const sharedProcessingQuery = useQuery(sharedProcessingByIdQueryOptions(sharedProcessingId));
  const sharedOrganisationsQuery = useQuery(sharedProcessingSharedOrganisationsQueryOptions(sharedProcessingId));
  const [organisationsToAdd, setOrganisationsToAdd] = useState<Array<SharedOrganisation> | null>(null);

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!sharedProcessingQuery.data || !sharedOrganisationsQuery.data) return <Drawer.Loading />;

        const sharedProcessing = sharedProcessingQuery.data;
        const has = sharedOrganisationsQuery.data.filter(organisation => organisation.isShared);
        const isApproved = Boolean(sharedProcessing.isDistributable);

        return (
          <Stack sx={{ height: "100%" }}>
            <SharedProcessingDrawerHeader
              activeTab="standard"
              statusIndicator="hidden"
              sharedProcessing={sharedProcessing}
            />
            <Drawer.SubDrawerHeader
              onBackClick={() => navigate("..")}
              renderIcon={props => <TenantIcon2 {...props} />}
              title={t("SharedProcessing.PresentIn.Header")}
              count={has.length}
            />
            <Drawer.Content sx={{ pt: 0 }}>
              {sharedOrganisationsQuery.data.length === 0 ? (
                <Stack sx={{ alignItems: "center", justifyContent: "center", gap: 0.5, py: 20, textAlign: "center" }}>
                  <Typography
                    sx={{ fontWeight: 600, fontSize: theme => theme.typography.pxToRem(14), color: "text.secondary" }}
                  >
                    {t("SharedProcessing.Share.NoOrganisations")}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: theme => theme.typography.pxToRem(12),
                      color: "text.secondary",
                      textWrap: "balance",
                    }}
                  >
                    {t("SharedProcessing.Share.NoOrganisationsDescription")}
                  </Typography>
                </Stack>
              ) : (
                <PresentInGroups
                  sharedProcessing={sharedProcessing}
                  organisations={sharedOrganisationsQuery.data}
                  isApproved={isApproved}
                  onAdd={setOrganisationsToAdd}
                />
              )}
            </Drawer.Content>
            {organisationsToAdd && (
              <ShareSharedProcessingDialog
                sharedProcessingId={sharedProcessing.id}
                organisations={organisationsToAdd}
                onClose={() => setOrganisationsToAdd(null)}
              />
            )}
          </Stack>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

const PresentInGroups = ({
  sharedProcessing,
  organisations,
  isApproved,
  onAdd,
}: {
  sharedProcessing: { id: string; status: { status: RegistrationStatus["status"] } };
  organisations: Array<SharedOrganisation>;
  isApproved: boolean;
  onAdd: (organisations: Array<SharedOrganisation>) => void;
}) => {
  const { t } = useTranslation();
  const notAdded = organisations.filter(organisation => !organisation.isShared);
  const present = organisations.filter(organisation => organisation.isShared);

  return (
    <Stack sx={{ gap: 3 }}>
      {!isApproved && (
        <Typography component="div" sx={{ fontSize: theme => theme.typography.pxToRem(14), color: "text.secondary" }}>
          <Translate translationKey="SharedProcessing.PresentIn.NotApproved" values={STATUS_WORDS} />
        </Typography>
      )}
      {notAdded.length > 0 && (
        <PresentInGroup
          label={t("SharedProcessing.PresentIn.Group.notAdded")}
          action={
            notAdded.length > 1 && (
              <Button
                variant="text"
                size="small"
                disabled={!isApproved}
                onMouseEnter={() => prefetchResponsibleCandidates(notAdded)}
                onClick={() => onAdd(notAdded)}
              >
                {t("SharedProcessing.PresentIn.AddToAll", { numberOfOrganisations: notAdded.length })}
              </Button>
            )
          }
        >
          {notAdded.map(organisation => (
            <OrganisationRow key={organisation.organisationId} name={organisation.organisationName}>
              <StatusOptionLabel status={sharedProcessing.status.status} />
              <Button
                variant="outlined"
                size="small"
                startIcon={<PlusIcon size={14} />}
                disabled={!isApproved}
                onMouseEnter={() => prefetchResponsibleCandidates([organisation])}
                onClick={() => onAdd([organisation])}
              >
                {t("SharedProcessing.PresentIn.Add")}
              </Button>
            </OrganisationRow>
          ))}
        </PresentInGroup>
      )}
      {present.length > 0 && (
        <PresentInGroup label={t("SharedProcessing.PresentIn.Group.present")}>
          {present.map(organisation => (
            <HasRow
              key={organisation.organisationId}
              organisation={organisation}
              sharedProcessingId={sharedProcessing.id}
            />
          ))}
        </PresentInGroup>
      )}
    </Stack>
  );
};

const PresentInGroup = ({ label, action, children }: { label: string; action?: ReactNode; children: ReactNode }) => (
  <Stack sx={{ gap: 0.75 }}>
    <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", gap: 2 }}>
      <Typography variant="h3">{label}</Typography>
      {action}
    </Stack>
    {children}
  </Stack>
);

const HasRow = ({
  organisation,
  sharedProcessingId,
}: {
  organisation: SharedOrganisation;
  sharedProcessingId: string;
}) => {
  const { t } = useTranslation();
  const currentCustomer = useCurrentCustomer();
  const { hasPermission } = usePermission();
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const removeMutation = useMutation(removeSharedProcessingFromOrganisationMutationOptions());
  const removeLabel = t("SharedProcessing.PresentIn.Remove", { organisation: organisation.organisationName });
  const target = customerOrganisation(currentCustomer, organisation.organisationId);
  const registerHref =
    target && hasPermission("nova-forms-api:registration:read", target.permissions)
      ? `/${target.slug}/registrations/${sharedProcessingId}`
      : undefined;

  return (
    <OrganisationRow
      name={organisation.organisationName}
      href={registerHref}
      subLine={t("SharedProcessing.PresentIn.AddedBy", {
        name: organisation.addedByCustomer ? currentCustomer.name : organisation.organisationName,
      })}
    >
      <AnswerState isAwaiting={Boolean(organisation.awaitingClaim)} />
      <Tooltip title={removeLabel}>
        <IconButton size="small" aria-label={removeLabel} onClick={() => setIsRemoveDialogOpen(true)}>
          <TrashCanIcon size={14} />
        </IconButton>
      </Tooltip>
      {isRemoveDialogOpen && (
        <RemoveSharedProcessingDialog
          title={t("SharedProcessing.PresentIn.RemoveTitle", { organisation: organisation.organisationName })}
          confirmLabel={t("SharedProcessing.PresentIn.RemoveConfirm")}
          {...(organisation.awaitingClaim
            ? {
                body: (
                  <Translate
                    translationKey="SharedProcessing.Remove.Unanswered"
                    values={{ organisation: <Strong>{organisation.organisationName}</Strong> }}
                  />
                ),
              }
            : { preview: { reason: "removedFromOrganisation" as const, recipient: organisation.organisationName } })}
          onClose={() => {
            setIsRemoveDialogOpen(false);
            removeMutation.reset();
          }}
          onConfirm={comment =>
            removeMutation
              .mutateAsync({ id: sharedProcessingId, organisationId: organisation.organisationId, comment })
              .then(() => setIsRemoveDialogOpen(false))
              .catch(() => undefined)
          }
          isPending={removeMutation.isPending}
          isError={removeMutation.isError}
          errorMessage={
            hasErrorCode(removeMutation.error, "ORGANISATION_HAS_NO_REGISTRATION_FORM")
              ? t("SharedProcessing.Remove.OrganisationHasNoRegistrationForm")
              : undefined
          }
        />
      )}
    </OrganisationRow>
  );
};

const AnswerState = ({ isAwaiting }: { isAwaiting: boolean }) => {
  const { t } = useTranslation();
  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: 1, flexShrink: 0 }}>
      <StatusSquare
        sx={theme => ({ bgcolor: isAwaiting ? theme.palette.brandOrange["500"] : theme.palette.success.main })}
      />
      <Typography sx={{ fontWeight: 600, fontSize: theme => theme.typography.pxToRem(12), color: "text.secondary" }}>
        {t(isAwaiting ? "SharedProcessing.NotInUseYet" : "SharedProcessing.InUse")}
      </Typography>
    </Stack>
  );
};

const OrganisationRow = ({
  name,
  href,
  subLine,
  children,
}: {
  name: string;
  href?: string;
  subLine?: ReactNode;
  children: ReactNode;
}) => (
  <Box
    sx={theme => ({
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      minHeight: 52,
      px: 2,
      py: 1.5,
      border: "1px solid",
      borderColor: theme.palette.grey["300"],
      borderRadius: 1,
    })}
  >
    <Stack sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600, fontSize: theme => theme.typography.pxToRem(14), overflowWrap: "anywhere" }}>
        {href ? (
          <Link href={href} underline="hover" sx={{ font: "inherit" }}>
            {name}
          </Link>
        ) : (
          name
        )}
      </Typography>
      {subLine && (
        <Typography sx={{ fontSize: theme => theme.typography.pxToRem(12), color: "text.secondary" }}>
          {subLine}
        </Typography>
      )}
    </Stack>
    {children}
  </Box>
);
